// src/lib/components/atoms/InputNumber/InputNumberAtom.tsx
import React from 'react';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import './InputNumberAtom.scss';

export type InputNumberAtomType = 'decimal' | 'currency' | 'integer';

export interface InputNumberAtomProps {
  /** current numeric value (or null) */
  value: number | null;
  /** called with the new numeric value whenever it changes */
  onChange: (value: number | null) => void;

  /** 'decimal' | 'currency' | 'integer' */
  type?: InputNumberAtomType;

  /** ISO‐4217 currency code, e.g. "USD" (only used when type="currency") */
  currency?: string;

  /** BCP‑47 locale string, e.g. "en-US". Defaults to `navigator.language`. */
  locale?: string;

  /** Minimum / maximum value */
  min?: number;
  max?: number;

  /** Step increment. Defaults to `1` for integer; leave `undefined` otherwise */
  step?: number;

  /** Maximum decimal places allowed (from property rules) */
  precision?: number;

  /** A text to prepend or append (units, symbols, …) */
  prefix?: string;
  suffix?: string;

  /** Placeholder text shown when the input is empty */
  placeholder?: string;

  /** Show spinner up/down buttons? */
  showButtons?: boolean;
  /** Layout for the spinner buttons; pass `null` to omit entirely */
  buttonLayout?: 'horizontal' | 'vertical' | 'stacked' | null;

  /** `compact` is the 24px control for dense cards (spec rollup-combine D107); absent → today's 40px. */
  size?: 'default' | 'compact';

  /** disable the input? */
  disabled?: boolean;
  /** invalid state styling? */
  invalid?: boolean;

  /** Accessible name — required when no visible `<label>` is associated with `inputId` (this atom renders no label itself). */
  ariaLabel?: string;
  /** Id of the element that names the input (e.g. an adjacent eyebrow). */
  ariaLabelledBy?: string;
  /** Id of the element that describes the input (a hint or validation message). */
  ariaDescribedBy?: string;

  /** Passed through to the underlying inputId / className / style */
  inputId?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const InputNumberAtom: React.FC<InputNumberAtomProps> = ({
  value,
  onChange,
  type = 'decimal',
  currency,
  locale = navigator.language,
  min,
  max,
  step = 1,
  precision,
  prefix,
  suffix,
  placeholder,
  showButtons = false,
  buttonLayout = "stacked",
  size = 'default',
  disabled = false,
  invalid = false,
  inputId,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  className,
  style,
}) => {
  // coerce undefined ↦ null
  const handleValueChange = (e: InputNumberValueChangeEvent) => {
    onChange(e.value ?? null);
  };

  // PrimeReact wants a real number for step if you click spinner,
  // otherwise adding `undefined` yields NaN:
  const effectiveStep = type === 'integer' ? (step ?? 1) : step;

  // only pass a buttonLayout if it's not null
  const passedButtonLayout = buttonLayout ?? undefined;

  // Calculate maxFractionDigits based on type and precision
  const maxFractionDigits = type === 'integer' ? 0 : precision;

  return (
    <InputNumber
      inputId={inputId}
      // PrimeReact names its `role="spinbutton"` input from these attribute props.
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      value={value}
      onValueChange={handleValueChange}
      // currency mode only when requested
      {...(type === 'currency'
        ? { mode: 'currency' as const, currency }
        : {})}
      locale={locale}
      min={min}
      max={max}
      step={effectiveStep}
      maxFractionDigits={maxFractionDigits}
      prefix={prefix}
      suffix={suffix}
      placeholder={placeholder}
      showButtons={showButtons}
      buttonLayout={passedButtonLayout}
      disabled={disabled}
      invalid={invalid}
      className={[size === 'compact' ? 'p-inputnumber--compact' : '', className].filter(Boolean).join(' ') || undefined}
      style={style}
      // integer mode => no grouping; decimal/currency => default grouping
      useGrouping={type !== 'integer'}
    />
  );
};

export default InputNumberAtom;
